"use client";

import React from "react";
import { useAtomValue } from "jotai";
import { Swiper, SwiperSlide } from "swiper/react";
import { Mousewheel, Navigation } from "swiper/modules";
import { ChevronLeft } from "lucide-react";

import { Paragraph } from "@/components/ui/typography";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/card/product";
import Link from "@/components/link";

import { continuePurchaseProductsAtom } from "@/store/course";

import { cn } from "@/lib/utils";

function ContinuePurchase() {
  const products = useAtomValue(continuePurchaseProductsAtom);

  const prevNavRef = React.useRef(null);
  const nextNavRef = React.useRef(null);

  const [swiper, setSwiper] = React.useState(null);

  React.useEffect(() => {
    if (swiper && swiper.params) {
      swiper.params.navigation.prevEl = prevNavRef.current;
      swiper.params.navigation.nextEl = nextNavRef.current;
      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, [swiper, prevNavRef, nextNavRef]);

  if (!products?.length) {
    return (
      <Paragraph variant="b3" className="text-center my-8">
        No products to continue purchase.
      </Paragraph>
    );
  }

  return (
    <div className="relative block">
      <Swiper
        modules={[Navigation, Mousewheel]}
        slidesPerView="auto"
        spaceBetween={16}
        className="w-full"
        mousewheel={{ forceToAxis: true }}
        breakpoints={{
          900: {
            slidesPerView: 3,
            slidesOffsetBefore: 8,
            slidesOffsetAfter: 8,
          },
          600: {
            slidesPerView: 2,
            slidesOffsetBefore: 0,
          },
          350: {
            slidesPerView: 1,
            slidesOffsetBefore: 17,
            width: 280,
          },
        }}
        onSwiper={setSwiper}
      >
        {products.reverse().map((product) => (
          <SwiperSlide key={product.id}>
            <ProductCard
              as={Link}
              product={product}
              href={`/course-detail/${product.slug || product.courseSlug}`}
              showPrice
            />
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="absolute top-1/2 w-full flex justify-between z-50 pointer-events-none">
        <Button
          ref={prevNavRef}
          variant="outline"
          size="icon"
          className={cn(
            "bg-primary-bg border-primary-bg-strong rounded-full transition-colors duration-300",
            "disabled:invisible -translate-x-1/2 -translate-y-1/2 shadow pointer-events-auto"
          )}
        >
          <ChevronLeft className="size-5" />
        </Button>

        <Button
          ref={nextNavRef}
          variant="outline"
          size="icon"
          className={cn(
            "bg-primary-bg border-primary-bg-strong rounded-full transition-colors duration-300",
            "disabled:invisible translate-x-1/2 -translate-y-1/2 shadow pointer-events-auto"
          )}
        >
          <ChevronLeft className="size-5 rotate-180" />
        </Button>
      </div>
    </div>
  );
}

export default ContinuePurchase;
